import { useEffect, useMemo, useRef } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { getBankCards } from 'services/bankManagements'
import { createWithdrawalRequest, getMyDeposits } from 'services/deposits'
import { toman } from 'utilities/helper/format'
import WizardChrome, {
    IReceiptLine,
} from 'pages/loans/manager/grant/components/WizardChrome'
import StickyAction from 'pages/loans/manager/grant/components/StickyAction'
import useWizardHistory from 'pages/loans/manager/grant/useWizardHistory'
import { PulseCard, SectionError } from 'components/states'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import useWithdrawalDraft from './useWithdrawalDraft'
import { withdrawalTrackingPath } from './trackingPath'
import StepFund from './steps/StepFund'
import StepAmount from './steps/StepAmount'
import StepTerms from './steps/StepTerms'
import StepReview from './steps/StepReview'

const TOTAL_STEPS = 4
const MIN_AMOUNT = 1_000

const STEP_TITLES: Record<number, { title: string; subtitle?: string }> = {
    1: {
        title: 'از کدام صندوق برداشت می‌کنید؟',
        subtitle:
            'فقط صندوق‌هایی که در آن‌ها سرمایه دارید. عضویتتان در صندوق دست‌نخورده می‌ماند.',
    },
    2: { title: 'چه مبلغی لازم دارید؟' },
    3: { title: 'چطور دریافت کنید؟' },
    4: { title: 'توضیح و ثبت درخواست' },
}

type IWithdrawalErrorKind =
    | 'archived'
    | 'already_open'
    | 'amount_invalid'
    | 'over_capital'
    | 'network'
    | 'unknown'

/**
 * The four distinct 400s the create endpoint can answer with, as a closed set.
 *
 * It says why in Persian prose and sends nothing machine-readable, so the copy
 * is what there is to match on; each substring here is the part of one sentence
 * that the other three do not contain. Anything unrecognised stays `unknown`
 * rather than being guessed at — a failure funnel that quietly folds a new
 * error into an old bucket is worse than one with an honest gap.
 */
const errorKindOf = (error: any): IWithdrawalErrorKind => {
    if (!error?.response) return 'network'
    const message = String(error.response.data?.params ?? '')
    if (message.includes('بایگانی')) return 'archived'
    if (message.includes('درخواست برداشت باز')) return 'already_open'
    if (message.includes('وارد کنید')) return 'amount_invalid'
    if (message.includes('بیشتر است')) return 'over_capital'
    return 'unknown'
}

/**
 * How the member reached `step`. The event fires on every transition, so a
 * phone back button re-reports a step already seen; without this a step funnel
 * counts that as fresh traffic and reads as churn that never happened.
 */
const directionTo = (previous: number | null, step: number) => {
    if (previous === null || step === previous || step === previous + 1)
        return 'forward'
    if (step === previous - 1) return 'back'
    return 'jump'
}

/**
 * درخواست برداشت, as a wizard.
 *
 * What this replaces is not a worse screen — it is the absence of one. The
 * only way to get money out of a fund was «درخواست تسویه و خروج», which is
 * all-or-nothing by construction: a member who needed 1,000,000 of the
 * 10,000,000 they held had to ask to leave to get it. The feature is the
 * partial ask; the wizard is so that four small questions never arrive as one
 * intimidating form about the member's own money.
 *
 * Step lives in `?step=` rather than state alone so the phone's back button
 * walks the wizard instead of abandoning it, and answered steps collapse into
 * tappable receipt lines — same contract as the اعطای وام wizard, whose chrome
 * this reuses outright.
 */
export default function WithdrawRequestWizard() {
    const params = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()

    const step = Math.min(
        Math.max(Number(searchParams.get('step') || 1), 1),
        TOTAL_STEPS
    )

    const { draft, patch, clear } = useWithdrawalDraft()
    const {
        data: deposits,
        isLoading,
        isError,
        refetch,
    } = useQuery(['my-deposits'], getMyDeposits, { staleTime: 60_000 })
    // same key as StepTerms so react-query serves one request for both
    const { data: cardsData } = useQuery(['bank-cards'], () => getBankCards())

    // a fund in the route wins over the draft: arriving from a specific fund's
    // page means the question is already answered
    const routeFundId = params?.id ? Number(params.id) : null
    const fundId = routeFundId ?? draft.fundId

    const funds = deposits?.funds ?? []
    const fund = funds.find((item) => item.moneypool_id === fundId)

    const previousStep = useRef<number | null>(null)
    useEffect(() => {
        const previous = previousStep.current
        previousStep.current = step
        posthog.capture('withdrawal_request_step_viewed', {
            fund_id: fundId,
            step,
            direction: directionTo(previous, step),
        })
    }, [step, fundId])

    // One linear stack of steps, shared with the loan wizards — so the arrow,
    // the receipt's «ویرایش» and the phone's back button always agree. This
    // was `navigate(-1)`, which walked FORWARD after a receipt jump.
    const stepNav = useWizardHistory({
        step,
        paramsFor: (next) => ({ step: String(next) }),
    })
    const goTo = (next: number) => stepNav.forward(next)
    const back = stepNav.back
    const finishFlow = useFinishFlow(FLOWS.withdrawRequest)

    // a deep link to a later step with no fund chosen is a dead end
    useEffect(() => {
        if (!isLoading && step > 1 && !fund) {
            stepNav.replace(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, fund, isLoading])

    // A wizard that cannot end in a request. An open request is one of the
    // API's four 400s; a route fund absent from a loaded `my-deposits` is one
    // the server does not list at all, which is an archived fund (or one they
    // hold nothing in) — either way the request cannot be filed.
    //
    // Both are counted; only the open-request case is turned back here, and
    // only past step 1. Step 1 already says so per fund, which is where it
    // belongs — home used to hand a single-fund member straight to step 2 and
    // skip the only screen that knew. An archived fund is already bounced by
    // the effect above. The route's fund is dropped on the way back because it
    // wins over the draft, so leaving it in the path would ignore whatever
    // they pick next. A failed load is not a blocked wizard.
    useEffect(() => {
        if (isLoading || isError || !routeFundId) return
        const reason = !fund
            ? 'archived'
            : fund.open_request
            ? 'open_request'
            : null
        if (!reason) return
        posthog.capture('withdrawal_request_blocked', {
            fund_id: routeFundId,
            step,
            reason,
        })
        if (reason === 'open_request' && step !== 1) {
            toast.info('برای این صندوق یک درخواست برداشت باز دارید')
            navigate('/fund/withdraw-request?step=1', { replace: true })
        }
        // Keyed on the fund, not the step: one doomed wizard is one event, not
        // one per step the member walks through it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeFundId, fund, isLoading, isError])

    const amount = Number(draft.amount || 0)
    const capital = fund?.capital ?? 0
    const debt = fund?.debt ?? 0

    const { mutate: submit, isLoading: isSubmitting } = useMutation({
        mutationFn: () =>
            createWithdrawalRequest({
                moneypoolId: fundId as number,
                amount,
                partial_accepted: draft.partialAccepted,
                user_description: draft.note,
                selected_card_number: draft.cardId,
            }),
        onSuccess: (created) => {
            posthog.capture('withdrawal_request_submitted', {
                fund_id: fundId,
                amount,
                partial_accepted: draft.partialAccepted,
                has_note: Boolean(draft.note.trim()),
            })
            clear()
            queryClient.invalidateQueries(['my-deposits'])
            toast.success('درخواست برداشت ثبت شد و به مدیران اطلاع داده شد')
            // Every step of the wizard is spent. Replacing only the last one
            // left the others under the request, and back from it reopened
            // step 3.
            finishFlow(
                `/fund/withdraw-request/detail/${created.id}?fund=${fundId}`
            )
        },
        onError: (error: any) => {
            const reason = error?.response?.data?.params
            posthog.capture('withdrawal_request_failed', {
                fund_id: fundId,
                step,
                status: error?.response?.status,
                error_kind: errorKindOf(error),
                // the server's own sentence, so a rise in `unknown` can be read
                // rather than only counted. It is fixed copy, never user text.
                reason: typeof reason === 'string' ? reason : undefined,
            })
            toast.error(reason || 'ثبت درخواست انجام نشد')
        },
    })

    const receipt = useMemo<IReceiptLine[]>(() => {
        const lines: IReceiptLine[] = []
        if (step > 1 && fund) {
            lines.push({
                label: 'صندوق',
                value: fund.moneypool_name,
                step: 1,
            })
        }
        if (step > 2 && amount > 0) {
            lines.push({ label: 'مبلغ', value: toman(amount), step: 2 })
        }
        if (step > 3) {
            lines.push({
                label: 'پرداخت بخشی',
                value: draft.partialAccepted ? 'قابل قبول' : 'قابل قبول نیست',
                step: 3,
            })
        }
        return lines
    }, [step, fund, amount, draft.partialAccepted])

    const canContinue = () => {
        if (step === 2) return amount >= MIN_AMOUNT && amount <= capital
        return true
    }

    const onContinue = () => {
        if (!canContinue()) {
            if (amount > capital) {
                toast.error('مبلغ از سرمایهٔ شما در این صندوق بیشتر است')
            } else {
                toast.error('مبلغ درخواست را وارد کنید')
            }
            return
        }
        goTo(step + 1)
    }

    const selectedCard = (cardsData?.results ?? []).find(
        (card) => card.id === draft.cardId
    )
    const { title, subtitle } = STEP_TITLES[step]

    return (
        <WizardChrome
            flowTitle="درخواست برداشت"
            title={title}
            subtitle={subtitle}
            step={step}
            totalSteps={TOTAL_STEPS}
            receipt={receipt}
            onBack={back}
            onJumpTo={stepNav.backTo}
        >
            {isLoading && <PulseCard />}
            {isError && <SectionError onRetry={() => refetch()} />}

            {!isLoading && step === 1 && (
                <StepFund
                    funds={funds}
                    selectedId={fundId}
                    onSelect={(picked) => {
                        patch({ fundId: picked.moneypool_id })
                        goTo(2)
                    }}
                    onTrack={(blocked) => {
                        posthog.capture('withdrawal_request_opened', {
                            source: 'wizard',
                            blocked: true,
                            open_requests_count: blocked.length,
                        })
                        navigate(withdrawalTrackingPath(blocked))
                    }}
                />
            )}
            {!isLoading && step === 2 && fund && (
                <StepAmount
                    capital={capital}
                    debt={debt}
                    amount={draft.amount}
                    onChange={(value) => patch({ amount: value })}
                />
            )}
            {!isLoading && step === 3 && fund && (
                <StepTerms
                    amount={amount}
                    partialAccepted={draft.partialAccepted}
                    cardId={draft.cardId}
                    onChange={patch}
                />
            )}
            {!isLoading && step === 4 && fund && (
                <StepReview
                    fundName={fund.moneypool_name}
                    amount={amount}
                    capital={capital}
                    debt={debt}
                    partialAccepted={draft.partialAccepted}
                    cardLabel={
                        selectedCard
                            ? `${selectedCard.bank_name} — ${selectedCard.card_number}`
                            : null
                    }
                    note={draft.note}
                    onNoteChange={(value) => patch({ note: value })}
                />
            )}

            {/* step 1 advances by picking a fund, so it gets no «ادامه» bar */}
            {step > 1 && step < TOTAL_STEPS && (
                <StickyAction>
                    <button
                        type="button"
                        onClick={onContinue}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        ادامه
                    </button>
                </StickyAction>
            )}

            {step === TOTAL_STEPS && fund && (
                <StickyAction
                    summary={
                        <p className=" rounded-xl bg-brand-soft px-3 py-2 text-[11px] font-bold text-brand-deep">
                            درخواست {toman(amount)} از {fund.moneypool_name}
                        </p>
                    }
                >
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                            // the denominator: every submitted/failed pair has
                            // a tap, and the taps with neither are the ones
                            // that never reached the server at all
                            posthog.capture(
                                'withdrawal_request_submit_tapped',
                                {
                                    fund_id: fundId,
                                    amount,
                                    partial_accepted: draft.partialAccepted,
                                    has_note: Boolean(draft.note.trim()),
                                    has_card: Boolean(draft.cardId),
                                }
                            )
                            submit()
                        }}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white disabled:opacity-60"
                    >
                        {isSubmitting ? 'در حال ثبت…' : 'ثبت درخواست'}
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
